import { FalconUIRenderingContext, SourceLanguage, Link, Menu } from '@nutmeglabs/falcon-ui';
import Image from 'next/image';
import { ReduxState } from 'ducks';
import { selectAPILanguageOptions } from 'ducks/server/settings';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { useQueryString } from 'hooks/useQueryString';
import { appendQueryString } from 'lib/util/appendQueryString';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { selectIsPMP } from 'lib/util/privateMarketplace';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getFalconUIImageUrl, getImageUrl } from 'lib/util/imageUrl';
import { quantize } from 'lib/util/quantize';

const ImageComponent = ({ src }: { src: string }) => {
  return <Image fill src={src} loader={({ src, width }) => getImageUrl(src, width)} alt="banner" />;
};

interface Props {
  children: React.ReactNode;
}

export const FalconUIContextProvider = ({ children }: Props) => {
  const { t, i18n } = useTranslation();
  const currencyFormatter = useCurrencyFormatter();

  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedLanguage = Object.entries(languageOptionMap).find(([key, value]) => {
    return value.iso === i18n.language;
  })?.[0] as SourceLanguage;

  const languageOptions = useSelector(selectAPILanguageOptions);

  const handleLanguageChange = (lang: SourceLanguage) => {
    const urlParts = window.location.href.split('?');
    const searchParams = new URLSearchParams(urlParts.length > 1 ? urlParts[1] : '');
    searchParams.set('lng', languageOptionMap[lang].iso);

    window.location.href = `${urlParts[0]}?${searchParams.toString()}`;
  };

  const queryString = useQueryString();

  const isPMP = useSelector(selectIsPMP);

  return (
    <FalconUIRenderingContext.Provider
      value={{
        companyName: settings.supplier_name || '',
        companyLogoUrl: settings.logo_url || '',
        language: selectedLanguage,
        setLanguage: handleLanguageChange,
        supportedLanguages: languageOptions,
        t: (key: string, args?: Record<string, string>) => {
          switch (key) {
            case 'Sun':
              return t('Sun');
            case 'Mon':
              return t('Mon');
            case 'Tue':
              return t('Tue');
            case 'Wed':
              return t('Wed');
            case 'Thu':
              return t('Thu');
            case 'Fri':
              return t('Fri');
            case 'Sat':
              return t('Sat');
            case 'Days of Week: {{daysOfWeek}}':
              return t('Days of Week: {{daysOfWeek}}', args);
            case 'Hours: {{operatingHours}}':
              return t('Hours: {{operatingHours}}', args);
          }

          return t(key);
        },
        formatCurrencyAmount: currencyFormatter.format,

        linkFormatter: (link?: Link): string => {
          if (!link) {
            return '';
          }
          switch (link.type) {
            case 'PRODUCT':
              return isPMP
                ? appendQueryString(`/top/products/${link.productId}`, queryString)
                : appendQueryString(`/products/${link.productId}`, queryString);
            case 'CUSTOM_PAGE':
              return appendQueryString(link.customPagePath || '', queryString);
            case 'FULL_URL':
              return link.fullUrl || '';
            case 'PRIVATE_MARKETPLACE':
              return appendQueryString(link.privateMarketplacePath || '', queryString);
          }
          return '';
        },

        getMenuTitle: (menu: Menu): string => {
          return menu.title;
        },
        Image: ImageComponent,
        getImageUrl: ({ src, width, height }: { src: string; width?: number; height?: number }) => {
          return getFalconUIImageUrl({
            src,
            width: width ? quantize(width * 1.5, [2000, 1600, 1200, 800]) : undefined,
            height: height ? quantize(height * 1.5, [2000, 1600, 1200, 800]) : undefined,
          });
        },
      }}
    >
      {children}
    </FalconUIRenderingContext.Provider>
  );
};
